import { useEffect, useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { WFPButton } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EmailField } from "@/components/ui/email-field";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  createAdministratorSchema,
  EMPTY_ADMINISTRATOR_VALUES,
  type AdministratorFormValues,
} from "../../schema";
import type { SelectOption } from "../../types";

const ADMINISTRATOR_FORM_ID = "administrator-request-form";

const TEXT_FIELDS = [
  { name: "first_name", labelKey: "fieldName.firstName" },
  { name: "middle_name", labelKey: "fieldName.middleName" },
  { name: "last_name", labelKey: "fieldName.lastName" },
  { name: "contact_phone", labelKey: "fieldName.phoneNumber" },
  { name: "location", labelKey: "fieldName.location" },
  { name: "identification_card_number", labelKey: "fieldName.identificationCardNumber" },
  { name: "position", labelKey: "fieldName.position" },
] as const;

interface AdministratorModalProps {
  isOpen: boolean;
  genders: SelectOption[];
  initialValues?: AdministratorFormValues;
  onValidate: (values: AdministratorFormValues) => void;
  onClose: () => void;
}

export const AdministratorModal = ({
  isOpen,
  genders,
  initialValues,
  onValidate,
  onClose,
}: AdministratorModalProps) => {
  const { t } = useTranslation();

  const schema = useMemo(
    () => createAdministratorSchema(t("traderDetails.form.invalidEmailError")),
    [t]
  );

  const { control, handleSubmit, reset } = useForm<AdministratorFormValues>({
    defaultValues: EMPTY_ADMINISTRATOR_VALUES,
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (isOpen) {
      reset(initialValues ?? EMPTY_ADMINISTRATOR_VALUES);
    }
  }, [initialValues, isOpen, reset]);

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogContent className="sm:max-w-3xl" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>
            {t("representativeAggregatorCreationRequestDetails.administratorModal.title")}
          </DialogTitle>
        </DialogHeader>

        <form
          id={ADMINISTRATOR_FORM_ID}
          noValidate
          className="grid gap-5 sm:grid-cols-2"
          onSubmit={handleSubmit(onValidate)}
        >
          {TEXT_FIELDS.map(({ name, labelKey }) => (
            <Controller
              key={name}
              control={control}
              name={name}
              render={({ field, fieldState }) => (
                <Field>
                  <FieldLabel htmlFor={name}>{t(labelKey)}</FieldLabel>
                  <Input
                    {...field}
                    value={field.value ?? ""}
                    id={name}
                    aria-invalid={!!fieldState.error}
                    className="border-border"
                  />
                  <FieldError>{fieldState.error?.message}</FieldError>
                </Field>
              )}
            />
          ))}

          <EmailField control={control} name="email" label={t("fieldName.email")} />

          <Controller
            control={control}
            name="gender"
            render={({ field, fieldState }) => (
              <Field>
                <FieldLabel htmlFor="gender">{t("fieldName.gender")}</FieldLabel>
                <Select value={field.value ?? ""} onValueChange={field.onChange}>
                  <SelectTrigger
                    id="gender"
                    className="w-full border-border"
                    aria-invalid={!!fieldState.error}
                  >
                    <SelectValue placeholder={t("fieldName.gender")} />
                  </SelectTrigger>
                  <SelectContent>
                    {genders.map(gender => (
                      <SelectItem key={gender.value} value={String(gender.value)}>
                        {gender.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FieldError>{fieldState.error?.message}</FieldError>
              </Field>
            )}
          />

          <Controller
            control={control}
            name="day_of_birth"
            render={({ field, fieldState }) => (
              <Field>
                <FieldLabel htmlFor="day_of_birth">{t("fieldName.dateOfBirth")}</FieldLabel>
                <DatePicker
                  id="day_of_birth"
                  value={field.value}
                  onChange={field.onChange}
                  placeholder={t("fieldName.dateOfBirth")}
                  invalid={!!fieldState.error}
                />
                <FieldError>{fieldState.error?.message}</FieldError>
              </Field>
            )}
          />
        </form>

        <DialogFooter>
          <WFPButton type="button" variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </WFPButton>
          <WFPButton type="submit" form={ADMINISTRATOR_FORM_ID}>
            {t("common.validate")}
          </WFPButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
